/**
 * **See it, for staff** — `/site/booking/:flowId/preview` (w19e, T-5.65;
 * reshaped by C20, ADR-0040 §1 and §5, Wave 20C, T-5.68). The owner's ruling
 * of 2026-09-27 (spec §10.11): *"Staff may preview a draft flow, through a
 * staff-authenticated read. The public read still answers 404 for a draft."*
 *
 * ## Why a staff page and not the booking page in a preview mode
 *
 * The booking page (`/book/<slug>`) lives **on the firm's own domain**, and
 * the staff session lives in **this** bundle, on the platform host (ADR-0008:
 * two origins). A staff token outside the staff app's origin is exactly what
 * the two-origin split exists to prevent, so *See it* for a draft opens this
 * page, reading `GET /api/v1/booking-flows/{id}/preview` (`booking:can_view`).
 *
 * ## What it shows, and what it will not do
 *
 * The page as a visitor meets it, in the firm's words and in ADR-0040 §1's
 * fixed order: **your details** (name, email, a mobile if asked, the firm's
 * questions — *Send code* shows the code field in place, as the real page
 * does), **choose**, **pay** when payment is on, and **done**. Fields can be
 * typed into and a plan chosen, and nothing is kept. **No code is sent, and
 * submitting and paying are off**; the banner says so.
 */

import { Alert, Button, Card, CardBody, Checkbox, Input, Select, Skeleton, Textarea } from '@fiveox/ui'
import { Eye } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { EditPage } from '../../../components/edit'
import { StatusBadge } from '../../../components/page'
import { ApiError } from '../../../lib/api'
import { rupees } from '../../../lib/api/billing'
import { type FlowQuestion, type FlowPreview as Preview, type PreviewPlan, booking } from '../../../lib/api/booking'

/** The banner's words — one place, because a test reads them. */
export const PREVIEW_BANNER = 'This is a preview. Nothing here is saved or sent — no code goes out, and paying is off.'

/** Why a disabled button is disabled. */
export const PREVIEW_OFF = 'Off in a preview.'

export type PreviewPart = 'details' | 'choose' | 'pay' | 'done'

export const PART_LABELS: Record<PreviewPart, string> = {
  details: 'Your details',
  choose: 'Choose',
  pay: 'Pay',
  done: 'Done',
}

/** The parts a visitor meets, in ADR-0040 §1's order. No payment, no pay. */
export function previewParts(flow: Pick<Preview, 'payment'>): PreviewPart[] {
  return flow.payment ? ['details', 'choose', 'pay', 'done'] : ['details', 'choose', 'done']
}

/** A plan's price as the booking page words it. */
export function previewPrice(plan: PreviewPlan): string {
  if (plan.free) return 'Free'
  if (plan.price) return rupees(plan.price)
  if (!plan.payable) return 'Price on request'
  return 'Price not shown on your website'
}

export function SiteBookingPreview() {
  const { flowId = '' } = useParams()
  const [flow, setFlow] = useState<Preview | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setFlow(null)
    setError(null)
    booking
      .preview(flowId)
      .then((body) => live && setFlow(body.data))
      .catch((err: unknown) => {
        if (!live) return
        setError(
          err instanceof ApiError && err.status === 404
            ? 'This flow does not exist any more — it may have been deleted.'
            : err instanceof ApiError
              ? err.message
              : 'The preview could not be loaded. Reload the page to try again.',
        )
      })
    return () => {
      live = false
    }
  }, [flowId])

  const back = { to: `/site/booking/${flowId}`, label: 'Back to the flow' }

  if (error) {
    return (
      <EditPage title="Preview" back={back}>
        <Alert tone="error">{error}</Alert>
      </EditPage>
    )
  }
  if (!flow) {
    return (
      <EditPage title="Loading the preview…" back={back}>
        <Skeleton className="h-64 rounded-xl" />
      </EditPage>
    )
  }
  return <FlowPreviewView flow={flow} back={back} />
}

export function FlowPreviewView({ flow, back }: { flow: Preview; back: { to: string; label: string } }) {
  const parts = previewParts(flow)
  const [at, setAt] = useState(0)
  const part = parts[at] ?? 'details'
  const next = () => setAt((i) => Math.min(i + 1, parts.length - 1))
  const w = flow.wording

  return (
    <EditPage
      title={`Preview: ${flow.name}`}
      description={`What a visitor sees at ${flow.path} on your own website${flow.status === 'live' ? '' : ' once the flow is live'}.`}
      back={back}
      meta={<StatusBadge kind="flow" status={flow.status} />}
    >
      <Alert tone="info">
        <span className="inline-flex items-center gap-2" data-preview-banner="">
          <Eye size={15} aria-hidden /> {PREVIEW_BANNER}
        </span>
      </Alert>
      {flow.status === 'paused' && flow.message && (
        <Alert tone="warning">
          <span data-preview-paused="">The flow is paused, so visitors see this instead: “{flow.message}”</span>
        </Alert>
      )}

      <nav aria-label="Parts of the booking page" className="flex flex-wrap gap-2" data-preview-parts="">
        {parts.map((p, index) => (
          <Button
            key={p}
            size="sm"
            variant={index === at ? 'primary' : 'outline'}
            aria-current={index === at ? 'step' : undefined}
            onClick={() => setAt(index)}
          >
            {index + 1}. {PART_LABELS[p]}
          </Button>
        ))}
      </nav>

      <Card data-preview-part={part}>
        <CardBody className="space-y-5">
          <div>
            <h2 className="text-foreground text-lg font-semibold">{w.title}</h2>
            {w.intro && part === 'details' && <p className="text-muted-foreground mt-1 text-sm">{w.intro}</p>}
          </div>
          {part === 'details' && <Details flow={flow} onNext={next} />}
          {part === 'choose' && (
            <>
              <Plans flow={flow} />
              <Actions onBack={() => setAt(at - 1)}>
                {flow.payment ? (
                  <Button onClick={next} data-preview-next="">
                    {w.continue}
                  </Button>
                ) : (
                  // With payment off, this Continue sends the booking — off here.
                  <Off label={w.continue} />
                )}
              </Actions>
            </>
          )}
          {part === 'pay' && (
            <>
              <p className="text-muted-foreground text-sm">
                The visitor sees what they chose and its price, then pays through your payment gateway.
              </p>
              <Actions onBack={() => setAt(at - 1)}>
                <Off label={w.pay} />
              </Actions>
            </>
          )}
          {part === 'done' && (
            <div className="space-y-4 text-sm" data-preview-done="">
              <p>{w.thanks}</p>
              <Button disabled title={PREVIEW_OFF}>
                {w.done}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </EditPage>
  )
}

function Actions({ onBack, children }: { onBack: () => void; children: ReactNode }) {
  return (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
      <Button variant="ghost" onClick={onBack}>
        Back
      </Button>
      {children}
    </div>
  )
}

function Off({ label }: { label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className="text-muted-foreground text-xs">{PREVIEW_OFF}</span>
      <Button disabled title={PREVIEW_OFF} data-preview-off="">
        {label}
      </Button>
    </span>
  )
}

function Details({ flow, onNext }: { flow: Preview; onNext: () => void }) {
  const [sent, setSent] = useState(false)
  const w = flow.wording
  return (
    <div className="space-y-4">
      <Input name="preview-name" label="Your name" required />
      <Input name="preview-email" type="email" label="Email" required />
      {flow.ask.mobile && <Input name="preview-mobile" type="tel" label="Mobile number" required />}
      <Questions questions={flow.ask.questions} />
      {!sent ? (
        <Button onClick={() => setSent(true)} data-preview-send-code="">
          {w.send_code}
        </Button>
      ) : (
        <div className="space-y-4" data-preview-code="">
          <p className="text-muted-foreground text-xs">
            On the real page a code goes to their email{flow.ask.verify_mobile ? ' and their mobile' : ''}. Nothing is
            sent from a preview.
          </p>
          <Input name="preview-code" label="Code from your email" inputMode="numeric" maxLength={6} />
          {flow.ask.verify_mobile && (
            <Input name="preview-mobile-code" label="Code from your mobile" inputMode="numeric" maxLength={6} />
          )}
          <div className="flex justify-end">
            <Button onClick={onNext} data-preview-next="">
              {w.continue}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function Questions({ questions }: { questions: FlowQuestion[] }) {
  const [draft, setDraft] = useState<Record<string, string | boolean>>({})
  return (
    <>
      {questions.map((question) => {
        const name = `preview-${question.key}`
        const value = draft[question.key]
        const set = (next: string | boolean) => setDraft((all) => ({ ...all, [question.key]: next }))
        if (question.kind === 'checkbox') {
          return (
            <Checkbox
              key={question.key}
              name={name}
              label={question.label}
              checked={value === true}
              onChange={(event) => set(event.target.checked)}
            />
          )
        }
        if (question.kind === 'long_text') {
          return (
            <Textarea
              key={question.key}
              name={name}
              label={question.label}
              rows={3}
              required={question.required}
              value={typeof value === 'string' ? value : ''}
              onChange={(event) => set(event.target.value)}
            />
          )
        }
        if (question.kind === 'select') {
          return (
            <Select
              key={question.key}
              name={name}
              label={question.label}
              required={question.required}
              value={typeof value === 'string' ? value : ''}
              onChange={(event) => set(event.target.value)}
            >
              {/* C27 (UAT report #13): on a required question the empty
                  option is a placeholder, so a visitor cannot pick it back;
                  on an optional one it is the real "no answer". */}
              <option value="" disabled={question.required} hidden={question.required}>
                Choose one
              </option>
              {(question.options ?? []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          )
        }
        return (
          <Input
            key={question.key}
            name={name}
            type={question.kind === 'date' ? 'date' : 'text'}
            label={question.label}
            required={question.required}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => set(event.target.value)}
          />
        )
      })}
    </>
  )
}

function Plans({ flow }: { flow: Preview }) {
  const services = flow.services.filter((service) => service.plans.length > 0)
  if (!services.length) {
    return (
      <p className="text-muted-foreground text-sm">
        Nothing to choose from — no service this flow offers has an active plan.
      </p>
    )
  }
  return (
    <div className="space-y-4">
      {services.map((service) => (
        <div key={service.id} data-preview-service={service.id}>
          <p className="font-medium">{service.name}</p>
          {service.description && <p className="text-muted-foreground text-sm">{service.description}</p>}
          <ul className="divide-border border-border mt-2 divide-y rounded-lg border text-sm">
            {service.plans.map((plan) => (
              <li key={plan.id} className="flex flex-wrap items-baseline justify-between gap-x-3 px-3 py-2">
                <span>{plan.name}</span>
                <span className="text-muted-foreground" data-preview-price="">
                  {previewPrice(plan)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
